import { Controller } from "@hotwired/stimulus"

const SAVE_DELAY = 300

// Hide / Show for the synced "now playing" transcripts (TMS-1515). One choice
// covers every strip on the page — the stylesheet hides them off this
// controller's hidden value — and is saved for the student's next visit, on any
// course. Two kinds of control drive it: a video player's transcript button
// ("bar") and the text toggle above an audio strip ("text"). Playback is never
// touched; the strips keep following the media while hidden.
export default class extends Controller {
  static targets = ["control"]
  static values = { hidden: Boolean, saveUrl: String }

  disconnect() {
    if (this.saveTimeout) this.flushSave()
  }

  toggle() {
    this.hiddenValue = !this.hiddenValue
    this.scheduleSave()
  }

  hiddenValueChanged() {
    this.controlTargets.forEach((control) => this.syncControl(control))
  }

  controlTargetConnected(control) {
    this.syncControl(control)
  }

  syncControl(control) {
    const visible = !this.hiddenValue
    const label = visible ? control.dataset.hideLabel : control.dataset.showLabel
    const icon = control.querySelector("i")

    if (control.dataset.transcriptToggle === "bar") {
      control.setAttribute("aria-label", label)
      control.setAttribute("title", label)
      control.setAttribute("aria-pressed", String(visible))
      icon?.classList.toggle("ph-fill", visible)
      icon?.classList.toggle("ph", !visible)
      return
    }

    const text = control.querySelector("[data-transcript-toggle-label]")
    if (text) text.textContent = label
    control.setAttribute("aria-expanded", String(visible))
    icon?.classList.toggle("ph-eye-slash", visible)
    icon?.classList.toggle("ph-eye", !visible)
  }

  // Debounced so a quick hide-then-show sends only the choice the student landed
  // on — two requests in flight could arrive out of order and store the other one.
  scheduleSave() {
    if (this.saveTimeout) clearTimeout(this.saveTimeout)
    this.saveTimeout = setTimeout(() => this.flushSave(), SAVE_DELAY)
  }

  // Fire and forget, like the reader settings: the page has already changed, and
  // a failed save only costs the student the choice on their next visit. keepalive
  // lets a save still pending when the student navigates away finish the trip.
  flushSave() {
    clearTimeout(this.saveTimeout)
    this.saveTimeout = null
    if (!this.saveUrlValue) return

    fetch(this.saveUrlValue, {
      keepalive: true,
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
        "Accept": "application/json",
        "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content || ""
      },
      credentials: "same-origin",
      body: JSON.stringify({ preferences: { transcript_hidden: this.hiddenValue } })
    }).catch(() => {})
  }
}
